import React, { useState, useCallback, useImperativeHandle, ChangeEvent } from 'react';
import { InputGroup, InputProps as ChakraInputProps, SystemStyleObject } from '@chakra-ui/react';
import { DesignSystem } from '@everfi/design-system';
import { Icon, Layout, ClearInputButton, ShowHidePasswordButton, InputProps } from '../../';

type WithInputAddonsProps = InputProps & {
  icon?: {
    name?: string;
    ariaLabel?: string;
    onClick?: () => void;
  };
  showPassword?: boolean;
  toggleShowPassword?: () => void;
};

const MARGIN_PROPS = [
  'm',
  'margin',
  'mt',
  'marginTop',
  'mb',
  'marginBottom',
  'ml',
  'marginLeft',
  'mr',
  'marginRight',
  'ms',
  'marginStart',
  'me',
  'marginEnd',
  'mx',
  'marginX',
  'my',
  'marginY',
] as const;

type MarginProps = Pick<ChakraInputProps, (typeof MARGIN_PROPS)[number]>;

const splitMarginProps = <T extends MarginProps>(props: T): { margin: MarginProps; rest: T } => {
  const margin: Record<string, unknown> = {};
  const rest: T = { ...props };
  for (const key of MARGIN_PROPS) {
    if (key in rest) {
      margin[key] = rest[key];
      delete rest[key];
    }
  }
  return { margin, rest };
};

// v2 parity: elements were muted, input-height squares, narrowing to a padded fit only around a button.
const inputElementCss: SystemStyleObject = { px: 0, aspectRatio: '1', color: DesignSystem.Colors.TEXT_SUBDUED };
const startElementCss: SystemStyleObject = { ...inputElementCss, '&:has(button)': { aspectRatio: 'auto', ps: 1 } };
const endElementCss: SystemStyleObject = { ...inputElementCss, '&:has(button)': { aspectRatio: 'auto', pe: 1 } };

export function withInputAddons(componentName: string) {
  return (WrappedInput: React.ForwardRefExoticComponent<ChakraInputProps & React.RefAttributes<HTMLInputElement>>) => {
    const ComponentWithInputAddons = React.forwardRef<HTMLInputElement, WithInputAddonsProps>((props, forwardedRef) => {
      const {
        leftAddOn,
        rightAddOn,
        clearField,
        icon,
        type,
        showPassword,
        onChange,
        toggleShowPassword,
        readOnly,
        isReadOnly,
        ...inputProps
      } = props;
      // Margins go on the group: its elements are full-height, so an input margin would offset them.
      const { margin, rest } = splitMarginProps(inputProps);
      // The legacy name wins on conflict.
      const resolvedReadOnly = isReadOnly ?? readOnly;
      const inputRef = React.useRef<HTMLInputElement>(null);
      useImperativeHandle(forwardedRef, () => inputRef.current!, []);
      const [value, setValue] = useState(rest.value);
      const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
          onChange?.(event); // ensure input's original onChange gets called too
          setValue(event.target.value);
        },
        [onChange]
      );
      const handleClearField = useCallback(() => {
        clearField?.();
        setValue('');
      }, [clearField]);
      // Chakra's InputGroup pads the input whenever `endElement` is present, so it is passed only when
      // something occupies it; an always-rendered wrapper pads every input's end.
      const showClear = Boolean(value && value !== '' && !resolvedReadOnly);
      const passwordToggle =
        toggleShowPassword && showPassword !== undefined ? { toggleShowPassword, showPassword } : undefined;
      const hasEndElement = showClear || Boolean(rightAddOn) || Boolean(passwordToggle);
      return (
        <InputGroup
          {...margin}
          startElementProps={{ css: startElementCss }}
          endElementProps={{ css: endElementCss }}
          startElement={
            leftAddOn || type === 'search' ? (
              <>
                {type === 'search' && <Icon iconName="search" prefix="fas" />}
                {leftAddOn}
              </>
            ) : undefined
          }
          endElement={
            hasEndElement ? (
              <Layout variant="flex" alignContent="center">
                <Layout>{showClear && <ClearInputButton clearField={handleClearField} inputRef={inputRef} />}</Layout>
                {rightAddOn && <Layout alignSelf="center">{rightAddOn}</Layout>}
                {passwordToggle && <ShowHidePasswordButton {...passwordToggle} />}
              </Layout>
            ) : undefined
          }
        >
          <WrappedInput
            type={type}
            ref={inputRef}
            onChange={handleChange}
            {...rest}
            readOnly={resolvedReadOnly}
            css={!value && type === 'date' && 'padding-inline-end: var(--input-padding)'}
          />
        </InputGroup>
      );
    });
    ComponentWithInputAddons.displayName = componentName;
    return ComponentWithInputAddons;
  };
}
